import axios from "axios";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  admitRole?: string;
  /** Admit SQL Users.id — links to ExamProctorAssignment.proctorId */
  admitUserId?: number | null;
  studentId?: string;
}

export interface LoginResponse {
  message: string;
  user: User;
  token: string;
}

export interface RegisterResponse {
  message: string;
  user: User;
  token: string;
}

export const authService = {
  // Register user
  register: async (data: {
    name: string;
    email: string;
    password: string;
    role?: string;
    studentId?: string;
  }): Promise<RegisterResponse> => {
    const response = await api.post("/api/auth/register", data);
    
    // Store token
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
    }
    
    return response.data;
  },

  // Login user
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await api.post("/api/auth/login", { email, password });
    
    // Store token and user data
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
    }
    
    return response.data;
  },

  // Get current user
  getCurrentUser: async (): Promise<User> => {
    const token = localStorage.getItem("token");
    if (!token) {
      const storedUser = authService.getStoredUser();
      if (storedUser) {
        return storedUser;
      }
      throw new Error("Authentication required");
    }

    const storedUser = authService.getStoredUser();
    if (storedUser) {
      return storedUser;
    }

    const response = await api.get("/api/auth/me");
    return response.data.user;
  },

  // Refresh token
  refreshToken: async (): Promise<{ token: string }> => {
    const response = await api.post("/api/auth/refresh");
    
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
    }
    
    return response.data;
  },

  // Logout
  logout: (): void => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem("token");
  },

  // Get stored user
  getStoredUser: (): User | null => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  },

  // Get stored token
  getToken: (): string | null => {
    return localStorage.getItem("token");
  },

  // Login with passkey (URL-based student authentication)
  // Stores the token and user info returned by the backend startExam endpoint
  loginWithPasskey: (token: string, user: { id: string; name: string; email: string; role: string; studentId?: string }): void => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
  },
};

